import React, {useEffect} from "react";
import {Button, Chip, Grid, Tooltip, Typography} from "@mui/material";
import {Warning} from "@mui/icons-material";
import {
    approvalStatusColorMapper,
    getThreadDetails,
    listingStatusColorMapper,
    messageStatusColorMapper
} from "../../../../../utils/utils";
import Image from "../../../../../components/image";
import moment from "moment";
import {Link} from "react-router-dom";
import Auth from "../../../../../auth";

import { createFilter } from "../../../../../components/saved_search/utils/filterManager";
import { pageContentGap } from "../../../../../theme";

// region TypeScript Interfaces
// Catalog product / message rows are highly variable; we type the row prop as
// `any` because columns are reused across catalog, partner-catalog, message
// table and dashboard contexts where the row shape diverges.
interface ColumnRowProps {
    row: any;
    column?: any;
    colIndex?: number;
    rowIndex?: number;
    addFilter?: (filter: any) => void;
    noFilter?: boolean;
    style?: React.CSSProperties;
    isRetailer?: boolean;
    hoverTemplate?: any;
}

interface EventIdColumnProps {
    row: any;
    setEventId: (id: any) => void;
    setEventViewed: (viewed: any) => void;
    setEventDialogOpen: (open: boolean) => void;
}

interface MessageIdColumnProps {
    row: any;
    isRetailer: boolean;
}

interface ChipsHoverTemplateProps {
    items: any[];
    field: string;
    operator?: string;
    addFilter?: (filter: any) => void;
}

interface TagsHoverTemplateProps {
    tags: any[];
    addFilter?: (filter: any) => void;
}
// endregion

//region Custom Styles

// Export for backward compatibility
export const useTagPopoverStyles = () => ({
    tagPopover: 'styled-tag-popover'
});
//endregion

export const EventIdColumn = (props: EventIdColumnProps) => {
    const {row, setEventId, setEventViewed, setEventDialogOpen} = props;

    return (
        <Grid container justifyContent={"flex-end"} style={{padding: "12px"}}>
            <Button variant="outlined"
                    aria-label={`View event ${row.Id}`}
                    onClick={async () => {
                        setEventId(row.Id);
                        setEventViewed(row.Viewed);
                        setEventDialogOpen(true);
                    }}>
                View
            </Button>
        </Grid>
    );
}

export const ProductColumn = (props: ColumnRowProps) => {
    const {row, addFilter, noFilter} = props;

    return (
        <div style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            margin: "12px 0"
        }}>
            {/*Number Overlay (On Image)*/}
            <div style={{position: "relative"}}>
                {row.imageCount != null && row.imageCount !== 0 && (
                    <Typography style={{
                        position: "absolute",
                        left: "0px",
                        top: "0px",
                        margin: "5px",
                        width: "16px",
                        height: "16px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "white",
                        borderRadius: "6px"
                    }}>
                        {row.imageCount}
                    </Typography>
                )}

                <Grid container justifyContent={"center"} alignItems={"center"}
                      style={{
                          height: "63px", width: "63px", borderRadius: "6px",
                          border: "1px solid #D6D6D6"
                      }}>
                    {row.image == null ? (
                        <>
                            <Tooltip title={"No Image"}>
                                <Warning style={{color: "#D6D6D6"}}/>
                            </Tooltip>
                        </>
                    ) : (
                        <Image src={row.image} alt={"Placeholder"}
                               altImage={<Warning style={{width: "50%", height: "50%", color: "#D6D6D6"}}/>}
                               style={{
                                   maxWidth: "60px",
                                   height: "89%",
                                   objectFit: "cover",
                               }}/>
                    )}
                </Grid>
            </div>
            <Grid container style={{flexDirection: "column", margin: "0 12px", alignItems: "flex-start", display: "flex"}}>
                <Chip
                    size="small"
                    variant="outlined"
                    label={row.parentSku == null ? "N/A" : row.parentSku}
                    style={{
                        fontSize: "12px",
                        color: "#7F7D83",
                        height: "24px",
                    }}
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (row.parentSku && !noFilter && addFilter) {
                            addFilter({
                                field: "parentSku",
                                operator: "eq",
                                value: row.parentSku
                            });
                        }
                    }}
                />
                <Typography style={{fontSize: "14px"}}>
                    {row.name == null ? "Unknown Item" : row.name}
                </Typography>
            </Grid>
        </div>
    );
}

export const CategoryColumn = (props: ColumnRowProps) => {
    const {row} = props;
    return (
        <Grid container style={{flexDirection: "column", margin: "0"}}>
            <Typography style={{
                fontSize: "12px",
                color: "#7F7D83"
            }}>
                {row.attributeSet == null ? "Unknown Attribute Set" : row.attributeSet}
            </Typography>
            <Typography style={{fontSize: "14px"}}>
                {row.category == null ? "Unknown Category" : row.category}
            </Typography>
        </Grid>
    );
}

export const ChipsHoverTemplate = ({ items, field, operator = "anyeq", addFilter }: ChipsHoverTemplateProps) => (
    <Grid container style={{ gap: "8px", flexWrap: "wrap", padding: "2px 0" }}>
        {items.map((item, index) => (
            <Chip
                key={`${item}-${index}`}
                size="small"
                variant="outlined"
                onClick={addFilter ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    addFilter({
                        field: field,
                        operator: operator,
                        value: item
                    });
                } : undefined}
                style={{
                    maxWidth: "125px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: addFilter ? "pointer" : "default"
                }}
                label={item}
            />
        ))}
    </Grid>
);

export const TagsHoverTemplate = ({ tags, addFilter }: TagsHoverTemplateProps) => (
    <ChipsHoverTemplate items={tags} field="tags" operator="anyeq" addFilter={addFilter} />
);

export const TagsColumn = (props: ColumnRowProps) => {
    const {row, addFilter} = props;
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    if (!row.tags || row.tags.length === 0) return <></>;

    const visibleTags = row.tags.slice(0, 2);
    const hasOverflow = row.tags.length > 2;

    return (
        <div ref={containerRef} style={{ display: "flex", gap: 4, overflow: "hidden", maxWidth: 180 }}>
            {visibleTags.map((tag: any, index: number) => (
                <Chip
                    key={`${tag}-${index}`}
                    size="small"
                    variant="outlined"
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (addFilter) {
                            addFilter({
                                field: "tags",
                                operator: "anyeq",
                                value: tag
                            });
                        }
                    }}
                    style={{
                        maxWidth: "125px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                    }}
                    label={tag}
                />
            ))}
            {hasOverflow && (
                <Tooltip
                    title={<TagsHoverTemplate tags={row.tags} addFilter={addFilter} />}
                    placement="bottom-start"
                    {...({ interactive: true } as any)}
                    componentsProps={{
                        tooltip: {
                            sx: {
                                background: "#fff",
                                color: "#222",
                                border: "1px solid #e0e0e0",
                                boxShadow: "0px 1px 8px rgba(0,0,0,0.12)",
                                borderRadius: "6px",
                                padding: "8px 12px",
                                fontSize: 14,
                                maxWidth: 320,
                                minWidth: 180,
                            }
                        }
                    }}
                >
                    <Chip size="small" variant="outlined" label="..." />
                </Tooltip>
            )}
        </div>
    );
}

export const ParentSkuColumn = (props: ColumnRowProps) => {
    const {row, addFilter} = props;
    if(row.parentSku == null || row.parentSku === "") return (<></>);
    return (
        <Grid container>
            <Chip key={`${row.parentSku}`} size={"small"}
                  variant="outlined"
                  style={{
                      maxWidth: "125px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                  }}
                  onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (addFilter) {
                          addFilter({
                              field: "parentSku",
                              operator: "eq",
                              value: row.parentSku
                          });
                      }
                  }}
                  label={row.parentSku}/>
        </Grid>
    )
}

export const LastUpdateColumn = (props: ColumnRowProps) => {
    const {row} = props;
    return (
        <Typography>{moment(row.lastUpdate).fromNow()}</Typography>
    )
}

export const CreatedDateColumn = (props: ColumnRowProps) => {
    const {row} = props;

    if(row.created == null) return (<></>);
    return (
        <Typography>{moment(row.created).fromNow()}</Typography>
    )
}

export const SupplierColumn = (props: ColumnRowProps) => {
    const {row} = props;
    // Body cells get padding "0 6px 0 0" when the table is selectable, which is
    // not enough to keep a supplier name off the next column. The other columns
    // in this table separate themselves the same way (see the status /
    // nextAvailableDate templates in catalog.tsx).
    return (
        <Typography style={{marginRight: "16px"}}>{row.supplierName}</Typography>
    )
}

export const MerchantColumn = (props: ColumnRowProps) => {
    const {row} = props;
    return (
        <Typography>{row.merchantName}</Typography>
    )
}

export const ListingStatusColumn = (props: ColumnRowProps) => {
    const {row, addFilter} = props;
    if (row.syncStatus === "" || row.syncStatus == null) return (<></>);
    return (
        <Chip label={row.syncStatus} size={"small"} onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (addFilter) addFilter(createFilter("syncStatus", "eq", row.syncStatus));
        }}
              style={{
                  color: "white",
                  backgroundColor: listingStatusColorMapper(row.syncStatus)
              }}/>
    )
}

export const ApprovalStatusColumn = (props: ColumnRowProps) => {
    const {row, addFilter} = props;
    return (
        <Chip label={row.approvalStatus} size={"small"} onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (addFilter) addFilter(createFilter("approvalStatus", "eq", row.approvalStatus));
        }}
              style={{
                  color: "white",
                  backgroundColor: approvalStatusColorMapper(row.approvalStatus)
              }}/>
    )
}

export const MerchantNameColumn = (props: ColumnRowProps) => {
    const {row, addFilter} = props;
    return (
        <Chip label={row.merchantName} size={"small"}
              style={{
                  background: "transparent",
                  border: "solid 1px gray",
                  maxWidth: "175px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"

              }}
              onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (addFilter) {
                      addFilter({
                          field: "merchantName",
                          operator: "eq",
                          value: row.merchantName
                      });
                  }
              }}
        />
    )
}

export const MessageStatusColumn = (props: ColumnRowProps) => {
    const {row, addFilter} = props;
    if (row.Status === "" || row.Status == null) return (<></>);
    return (
        <Chip label={row.Status} size={"small"} onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (addFilter) addFilter(createFilter("Status", "eq", row.Status));
        }}
              style={{
                  color: "white",
                  backgroundColor: messageStatusColorMapper(row.Status)
              }}/>
    )
}

export const MessageSkuColumn = (props: ColumnRowProps) => {
    const {row} = props;

    return (
        <Typography>{row.Reference}</Typography>
    )
}

export const MessageIdColumn = (props: MessageIdColumnProps) => {
    const {row, isRetailer} = props;

    const [partner, setPartner] = React.useState("");
    useEffect(() => {
        const getPartnerDetails = async () => {
            const threadDetails = await getThreadDetails(row.Id);
            if (threadDetails.ReceiptDetails.length > 0)
                setPartner(threadDetails.ReceiptDetails[0].Receiver);
        }

        // eslint-disable-next-line
        if (row.Sender == Auth.getAccountNumber())
            getPartnerDetails();
        else
            setPartner(row.Sender);

        // eslint-disable-next-line
    }, []);

    return (
        <Grid container justifyContent={"flex-end"} style={{padding: "12px", gap: pageContentGap}}>
            <Button variant="outlined"
                    component={Link}
                    to={`/message-center/thread?id=${row.Id}`}
            >
                View
            </Button>
            <Button variant="outlined"
                disabled={row.Reference == null}
                component={Link}
                to={`/productonboardingcenter/product?catalog=${isRetailer ? "partnercatalog" : "mycatalog"}&productId=${row.Reference}&partnerId=${partner}`}
            >
                Product
            </Button>
        </Grid>
    );
}

export const GroupsColumn = (props: ColumnRowProps) => {
    const { row, addFilter } = props;

    const groups = row.Groups || [];
    if (groups.length === 0) return (<></>);

    const visibleGroups = groups.slice(0, 2);
    const hasOverflow = groups.length > 2;

    return (
        <div style={{ display: "flex", gap: 4, overflow: "hidden", maxWidth: 180 }}>
            {visibleGroups.map((group: any, index: number) => (
                <Tooltip
                    key={`${group}-${index}`}
                    title={group}
                    placement="top"
                >
                    <Chip
                        size="small"
                        variant="outlined"
                        onClick={addFilter ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addFilter({
                                field: "Groups",
                                operator: "anyeq",
                                value: group
                            });
                        } : undefined}
                        style={{
                            maxWidth: "125px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: addFilter ? "pointer" : "default"
                        }}
                        label={group}
                    />
                </Tooltip>
            ))}
            {hasOverflow && (
                <Tooltip
                    title={<ChipsHoverTemplate items={groups} field="Groups" operator="anyeq" addFilter={addFilter} />}
                    placement="bottom-start"
                    {...({ interactive: true } as any)}
                    componentsProps={{
                        tooltip: {
                            sx: {
                                background: "#fff",
                                color: "#222",
                                border: "1px solid #e0e0e0",
                                boxShadow: "0px 1px 8px rgba(0,0,0,0.12)",
                                borderRadius: "6px",
                                padding: "8px 12px",
                                fontSize: 14,
                                maxWidth: 320,
                                minWidth: 180,
                            }
                        }
                    }}
                >
                    <Chip size="small" variant="outlined" label="..." />
                </Tooltip>
            )}
        </div>
    );
};
